/**
 * RequireAuth.jsx  —  FIXED
 *
 * ROOT CAUSE FIXED (Part 2 — Protected Action UX):
 *   When an unauthenticated user navigated directly to a protected route
 *   (deep link, back button, expired session), the component silently rendered
 *   <Navigate to="/login" /> with zero user feedback — no toast, no message.
 *
 * FIX STRATEGY:
 *   1. useEffect fires `toast.error('Please login to continue')` exactly once
 *      when the guard detects an unauthenticated / expired-session state.
 *   2. The render path still returns <Navigate /> immediately — no route flicker,
 *      no blank screen.
 *   3. Toast uses `sonner` (imported from 'sonner') — the same library used by
 *      Login.jsx for success/error messages. No new library added.
 *   4. Expired sessions are already handled upstream: AuthContext listens to the
 *      `auth:token-refresh-failed` window event and sets user → null, so
 *      isAuthenticated === false flows naturally through this guard.
 *   5. Toast is deduplicated with a stable `id` so rapid navigations or React
 *      StrictMode double-renders don't stack multiple identical toasts.
 *
 * UNCHANGED: loading state, inactive account redirect, email verification check,
 *            state.from preservation for post-login redirect.
 */

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Loader } from '@/shared/components/Loader';

const TOAST_ID = 'require-auth-login-prompt';

const RequireAuth = ({ requireVerified = false, requireActive = true }) => {
  const {
    isAuthenticated,
    user,
    loading,
    isVerified,
    isActive,
  } = useAuth();

  const location = useLocation();

  // ── Toast feedback for unauthenticated / expired-session access ──────────
  //
  // Fires after render so the Toaster (mounted in App.js) is already in the
  // DOM. The `id` prevents duplicate toasts on double-render (React StrictMode)
  // or when the user taps multiple protected links in quick succession.
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      toast.error('Please login to continue', { id: TOAST_ID });
    }
  }, [loading, isAuthenticated]);

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // NOT AUTHENTICATED  (also covers expired sessions)
  // ============================================

  if (!isAuthenticated) {
    // Preserve the intended destination so Login can redirect back after success.
    // Toast is already queued from the useEffect above.
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // ============================================
  // INACTIVE ACCOUNT CHECK
  // ============================================

  if (requireActive && !isActive) {
    return (
      <Navigate
        to="/account-disabled"
        state={{ from: location }}
        replace
      />
    );
  }

  // ============================================
  // EMAIL VERIFICATION CHECK
  // ============================================

  if (requireVerified && !isVerified) {
    return (
      <Navigate
        to="/verify-email-required"
        state={{ from: location }}
        replace
      />
    );
  }

  // ============================================
  // AUTHENTICATED — RENDER PROTECTED CONTENT
  // ============================================

  return <Outlet />;
};

export default RequireAuth;